import type { Vehicle, VehicleStatus } from '../../types/vehicle';
import type { AlertEvent, AlertCategory, AlertSeverity, AlertEvidence, AlertEvidenceView, EvidenceKind } from '../../types/alert';
import type { Driver, RiskLevel } from '../../types/driver';
import type { VehicleDto, AlertDto, AlertCanonicalTypeDto, DriverDto, EvidenceDto, AlertEvidenceDto, RankingEntryDto, OwnScoreDto, TripDto, TripStatusDto, TripTransitionDto, RouteDto,
  LogbookEntryDto, LogbookEntryTypeDto, LogbookTransitionDto, LogbookAttachmentDto,
  ExpenseDto, ExpenseCategoryDto, ExpenseTransitionDto, ReceiptDto } from './endpoints';
import type { LogbookEntry, LogbookEntryType, LogbookTransition, LogbookChange, LogbookAttachment } from '../../types/logbook';
import type { Expense, ExpenseCategory, ExpenseTransition, ExpenseChange, Receipt } from '../../types/expense';
import type { Trip, TripStatus, TripRoute, TripTransition, TripMetrics, TimeWindow } from '../../types/trip';
import type { DriverScore } from '../../types/driver';
import type { VehicleLiveDoc } from '../firestore/vehiclesLive';

// Traduce el schema REAL del backend (backend/services/core/api/openapi.yaml)
// a los tipos de dominio de la UI. Único lugar donde vive este mapeo — ver
// docs/frontend/ARCHITECTURE.md §13 (puntos de fricción identificados).

const VEHICLE_STATUS_FROM_STATE: Record<string, VehicleStatus> = {
  CRITICAL_EVENT: 'critical_event',
  NO_SIGNAL: 'no_signal',
  OUT_OF_SERVICE: 'offline',
  STOPPED: 'stopped',
  MOVING: 'in_route',
};

export function vehicleFromDto(dto: VehicleDto, subUnitName = '', deviceImei = ''): Vehicle {
  const pos = dto.lastPosition;
  return {
    id: dto.id,
    plate: dto.plate,
    model: dto.model ?? '',
    brand: dto.brand ?? '',
    year: dto.year ?? 0,
    status: VEHICLE_STATUS_FROM_STATE[dto.state] ?? 'no_signal',
    position: {
      lat: pos?.coords?.lat ?? 0,
      lng: pos?.coords?.lng ?? 0,
      heading: pos?.directionDeg ?? undefined,
      speed: pos?.speedKmh ?? undefined,
      timestamp: pos?.occurredAt ?? '',
    },
    driverId: dto.currentDriver?.driverId,
    driverName: dto.currentDriver?.displayName,
    subUnitId: dto.subunitId ?? '',
    // FE-04: resuelto por el llamador vía getSubunitNames() — el DTO solo
    // trae el id (join client-side, ver endpoints.ts).
    subUnitName,
    // HALLAZGO (registrado en PENDIENTES.md/FE-02): el schema `Vehicle` del
    // backend no entrega tenantId — el token no lo necesita (RLS ya aísla) y
    // ninguna pantalla lo muestra.
    tenantId: '',
    // issue #68 (reunión 2026-09-14): #68 se cerró en 2026-09-09 creyendo
    // resuelto ESTE campo, pero su fix (`ad9257b`) solo tocó la evidencia de
    // alertas y `AdminPage` — `vehicleFromDto` seguía fijando `''` siempre.
    // `deviceImei` sale de un JOIN client-side con `GET /devices` completo
    // (uno solo para toda la lista, por `installedVehicleId` — ver
    // `useVehicles.ts`), el mismo patrón que ya usa `subUnitName`.
    deviceId: deviceImei,
    activeAlerts: dto.activeAlertCount ?? 0,
    lastSeen: pos?.occurredAt ?? '',
    // FE-23 / BE-58: `documents` y `oilChange` se mapean AQUÍ cuando el contrato
    // los entregue (`openapi.yaml` todavía no los trae). Hasta entonces quedan
    // `undefined` y toda superficie dice "Sin datos" — nunca "vigente".
    documents: undefined,
    oilChange: undefined,
    // FE-19: el odómetro del equipo (BE-50). Llega en el listado Y en el
    // detalle: sale de la misma fila de estado que `lastPosition`, sin N+1.
    // `null` del contrato → `undefined` aquí: "todavía no se sabe", no cero.
    odometer: dto.odometer ?? undefined,
    // FE-14: solo vienen poblados cuando el DTO sale de `GET /vehicles/{id}`
    // (el detalle) — el listado los omite a propósito (N+1, ver types/vehicle.ts).
    // El vocabulario de `CameraLabel` es el mismo en ambos lados: sin mapeo.
    cameras: dto.cameras,
    hasLiveVideo: dto.hasLiveVideo,
  };
}

// FE-03: aplica una actualización en vivo de `vehicles-live` (ADR-008) sobre
// un vehículo ya cargado por REST. El documento vivo no trae plate/model/
// driver — solo se tocan posición/velocidad/estado/última vez visto.
// `heading` no viaja en el documento vivo: se conserva el último valor
// conocido de REST en vez de pisarlo con `undefined` (decisión laxa).
export function applyVehicleLiveUpdate(vehicle: Vehicle, live: VehicleLiveDoc): Vehicle {
  return {
    ...vehicle,
    status: VEHICLE_STATUS_FROM_STATE[live.state] ?? vehicle.status,
    position: {
      ...vehicle.position,
      lat: live.coords?.lat ?? vehicle.position.lat,
      lng: live.coords?.lng ?? vehicle.position.lng,
      speed: live.speedKmh ?? vehicle.position.speed,
      // #64: la hora de la posición y «visto hace X» salen de lastSeenAt (la
      // última señal REAL), nunca de updatedAt (cuándo escribimos el doc). Si
      // lastSeenAt falta, se conserva el último avistamiento que ya traía el
      // vehículo (de REST); caer en updatedAt reintroduciría el bug #59.
      timestamp: live.lastSeenAt ?? vehicle.position.timestamp,
    },
    lastSeen: live.lastSeenAt ?? vehicle.lastSeen,
  };
}

const ALERT_SEVERITY_FROM_DTO: Record<string, AlertSeverity> = {
  CRITICA: 'critical',
  MEDIA: 'medium',
  BAJA: 'low',
};

// Resuelto (BE-36): el contrato ahora publica AlertCanonicalType como
// catálogo cerrado de 8 valores. Este Record es exhaustivo sobre ese tipo
// generado — si el backend agrega un valor, esto deja de compilar en vez de
// caer a un fallback adivinado. Ver PENDIENTES.md/FE-02.
const ALERT_CATEGORY_FROM_CANONICAL: Record<AlertCanonicalTypeDto, AlertCategory> = {
  COLLISION: 'collision',
  FATIGUE: 'fatigue',
  HARSH_ACCELERATION: 'harsh_acceleration',
  HARSH_BRAKING: 'harsh_braking',
  HARSH_CORNERING: 'harsh_cornering',
  OVERSPEED: 'overspeed',
  OVERTIME_DRIVING: 'overtime_driving',
  SOS: 'sos',
  UNKNOWN: 'unknown',
};

export function alertFromDto(dto: AlertDto): AlertEvent {
  return {
    id: dto.id,
    vehicleId: dto.vehicleId ?? '',
    vehiclePlate: dto.vehiclePlate ?? '',
    driverId: dto.driverId ?? undefined,
    driverName: dto.driverName ?? undefined,
    category: ALERT_CATEGORY_FROM_CANONICAL[dto.canonicalType],
    subCategory: dto.category, // conserva el valor crudo del backend, sin descartarlo
    // BE-45: el contrato declara `source` requerido, así que el ternario no es
    // un fallback adivinado — es la lectura de un valor que el backend nunca
    // omite. El enum solo tiene DEVICE y HUMAN.
    source: dto.source === 'HUMAN' ? 'human' : 'device',
    severity: ALERT_SEVERITY_FROM_DTO[dto.severity] ?? 'low',
    status: dto.status === 'CLOSED' ? 'closed' : 'active',
    timestamp: dto.occurredAt,
    closedAt: dto.closedAt ?? undefined,
    closureReason: dto.closedReason ?? undefined,
    lat: dto.coords?.lat ?? 0,
    lng: dto.coords?.lng ?? 0,
    // HALLAZGO (mismo que en vehicleFromDto): no viajan en este DTO.
    subUnitId: '',
    subUnitName: '',
    tenantId: '',
    isEscalated: dto.status === 'ESCALATED',
  };
}

const EVIDENCE_KIND_FROM_DTO: Record<string, EvidenceKind> = {
  VIDEO: 'video',
  IMAGE: 'image',
};

// BE-37: mapeo directo, sin desajustes — `CameraLabel` ya es el mismo
// vocabulario en ambos lados (igual que en `vehicleFromDto`).
export function alertEvidenceViewFromDto(dto: AlertEvidenceDto): AlertEvidenceView {
  return {
    window: dto.window,
    coverage: dto.coverage,
    truncated: !!dto.truncatedReason,
    cameras: dto.cameras.map(camera => ({
      cameraId: camera.cameraId,
      label: camera.label,
      name: camera.name ?? undefined,
      coverage: camera.coverage,
      spans: camera.spans,
      gaps: camera.gaps,
      clips: camera.clips,
    })),
    items: dto.items.map(evidenceFromDto),
  };
}

export function evidenceFromDto(dto: EvidenceDto): AlertEvidence {
  return {
    id: dto.id,
    kind: EVIDENCE_KIND_FROM_DTO[dto.kind] ?? 'video',
    camera: dto.camera ?? undefined,
    capturedAt: dto.capturedAt,
    durationSeconds: dto.durationSeconds ?? undefined,
    retentionHold: dto.retentionHold,
  };
}

const RISK_LEVEL_FROM_DTO: Record<string, RiskLevel> = {
  BAJO: 'low',
  MEDIO: 'medium',
  ALTO: 'high',
};

// El schema `Driver` real solo entrega identidad (id, displayName,
// documentId, phone, subunitId, fleetId, userId): el handler de
// `GET /drivers`/`GET /drivers/{id}` nunca popula `score`, aunque el schema
// lo declare — BE-22 solo lo calcula en `/rankings`/`/me/score` (FE-16 los
// mezcla encima en `useDrivers()`). Este mapeo cubre el caso, hoy inerte, de
// que el backend empiece a mandarlo también aquí. Ningún endpoint entrega
// `trend` todavía — se fija en 'stable', no es un dato inventado sobre una
// métrica real. Los contadores de eventos/km del tipo de dominio quedan
// `undefined`: no existen en ningún endpoint hoy.
export function driverFromDto(dto: DriverDto, subUnitName = ''): Driver {
  const riskLevel = dto.score?.riskLevel ? RISK_LEVEL_FROM_DTO[dto.score.riskLevel] : undefined;
  return {
    id: dto.id,
    name: dto.displayName,
    phone: dto.phone ?? undefined,
    subUnitId: dto.subunitId ?? '',
    subUnitName,
    tenantId: '',
    userId: dto.userId ?? undefined,
    score: (dto.score?.value !== undefined && riskLevel)
      ? { value: dto.score.value, level: riskLevel, trend: 'stable', updatedAt: dto.score.periodStart ?? '' }
      : undefined,
  };
}

// FE-16: BE-22 entrega el score real por `/rankings` y `/me/score`, no por
// `GET /drivers` (ese endpoint nunca lo popula — ver `driverFromDto`). Ninguno
// de los dos da un `trend` histórico todavía, igual que arriba.
export function rankingEntryToScore(entry: RankingEntryDto): DriverScore | undefined {
  const level = RISK_LEVEL_FROM_DTO[entry.riskLevel];
  if (!level) return undefined;
  return { value: entry.score, level, trend: 'stable', updatedAt: '' };
}

export function ownScoreToDriverScore(dto: OwnScoreDto): DriverScore | undefined {
  const level = RISK_LEVEL_FROM_DTO[dto.riskLevel];
  if (!level) return undefined;
  return {
    value: dto.score,
    level,
    trend: 'stable',
    updatedAt: dto.periodStart,
    rankPosition: dto.rankPosition ?? undefined,
    rankTotal: dto.rankTotal ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// FE-11 — viajes (ADR-024)
// ---------------------------------------------------------------------------

const TRIP_STATUS_FROM_DTO: Record<TripStatusDto, TripStatus> = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const TRIP_STATUS_TO_DTO: Record<TripStatus, TripStatusDto> = {
  planned: 'PLANNED',
  in_progress: 'IN_PROGRESS',
  completed: 'COMPLETED',
  cancelled: 'CANCELLED',
};

/**
 * Una ventana solo sirve si tiene duración y una fecha creíble.
 *
 * Dos casos la anulan y los dos se resuelven AQUÍ, no en cada pantalla:
 *  - La colapsada de un viaje cancelado (`to == from`): no hubo trayecto.
 *  - La imposible del issue #93: hoy un cancelado devuelve
 *    `0001-01-01T00:00:00Z` en las dos marcas. Pintarla saca el año 1 en la
 *    UI. Es un defecto del backend y NO se parchea allí desde el front: se
 *    descarta el valor y se deja el viaje sin ventana, que es la verdad.
 */
function windowFromDto(from: string, to: string | undefined | null): TimeWindow | undefined {
  if (!from || !to) return undefined;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end)) return undefined;
  // Cualquier fecha anterior a la época Unix en este dominio es un valor cero
  // del backend, no un viaje de verdad.
  if (start <= 0 || end <= 0) return undefined;
  if (end <= start) return undefined;
  return { from, to };
}

export function tripFromDto(dto: TripDto): Trip {
  return {
    id: dto.id,
    vehicleId: dto.vehicleId,
    vehiclePlate: dto.vehiclePlate ?? '',
    subUnitId: dto.subunitId ?? undefined,
    driverId: dto.driverId ?? undefined,
    driverName: dto.driverName ?? undefined,
    origin: dto.origin,
    destination: dto.destination,
    scheduledFrom: dto.scheduledFrom,
    scheduledTo: dto.scheduledTo,
    actualFrom: dto.actualFrom ?? undefined,
    actualTo: dto.actualTo ?? undefined,
    window: windowFromDto(dto.validFrom, dto.validTo),
    status: TRIP_STATUS_FROM_DTO[dto.status],
    cancelledReason: dto.cancelledReason ?? undefined,
    createdBy: dto.createdBy,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt ?? undefined,
  };
}

export function tripRouteFromDto(dto: RouteDto): TripRoute {
  return {
    from: dto.from,
    to: dto.to,
    downsampled: dto.downsampled ?? false,
    points: dto.points.map(p => ({
      lat: p.coords.lat,
      lng: p.coords.lng,
      occurredAt: p.occurredAt,
      speedKmh: p.speedKmh ?? undefined,
    })),
  };
}

function tripWindowFromDto(w: { from: string; to: string } | undefined): TimeWindow | undefined {
  return w ? windowFromDto(w.from, w.to) : undefined;
}

/**
 * ⚠️ Ordena en cliente. El contrato promete "transiciones en orden
 * cronológico" y el backend no lo cumple (issue #94).
 *
 * Y ordenar por `occurredAt` NO basta — medido contra el viaje real de
 * `PAT001` el 2026-09-11. Ese campo mezcla DOS relojes distintos:
 *
 *   - `start`/`finish`/corrección traen el instante **declarado del hecho**
 *     (10:05, 14:00 — el viaje se registró retroactivamente).
 *   - La creación trae el instante en que **se insertó la fila** (21:54).
 *
 * Así que la creación sale después del fin del viaje por más que se ordene,
 * y la novedad cuenta que el viaje terminó antes de existir. La creación es,
 * por definición, el primer hecho del viaje: nada puede pasar antes de que la
 * fila exista. Se ancla primera y el resto se ordena por `occurredAt`.
 *
 * La creación es la única transición SIN `fromStatus` — el contrato lo marca
 * opcional justo por eso, así que reconocerla no es una heurística frágil.
 */
export function tripTransitionsFromDto(items: TripTransitionDto[]): TripTransition[] {
  const esCreacion = (t: TripTransition) => t.fromStatus === undefined;
  return items
    .map(dto => ({
      fromStatus: dto.fromStatus ? TRIP_STATUS_FROM_DTO[dto.fromStatus] : undefined,
      toStatus: TRIP_STATUS_FROM_DTO[dto.toStatus],
      fromWindow: tripWindowFromDto(dto.fromWindow),
      toWindow: tripWindowFromDto(dto.toWindow),
      actor: dto.actor,
      reason: dto.reason ?? undefined,
      occurredAt: dto.occurredAt,
    }))
    .sort((a, b) => {
      if (esCreacion(a) !== esCreacion(b)) return esCreacion(a) ? -1 : 1;
      return Date.parse(a.occurredAt) - Date.parse(b.occurredAt);
    });
}

/**
 * Métricas derivadas de la ruta REAL. La duración sale de la ventana del viaje
 * (lo que gobierna), no del primer y último punto: un viaje sin cobertura al
 * principio duró lo que duró, no lo que se alcanzó a medir.
 *
 * No calcula distancia. Sumar los tramos de la polyline daría un número que
 * parece kilometraje y no lo es — ver `TripMetrics`.
 */
export function tripMetricsFrom(trip: Trip, route: TripRoute | null): TripMetrics {
  const speeds = (route?.points ?? [])
    .map(p => p.speedKmh)
    .filter((s): s is number => typeof s === 'number');

  const durationMinutes = trip.window
    ? Math.max(0, Math.round((Date.parse(trip.window.to) - Date.parse(trip.window.from)) / 60000))
    : undefined;

  return {
    durationMinutes,
    avgSpeedKmh: speeds.length > 0 ? Math.round(speeds.reduce((a, b) => a + b, 0) / speeds.length) : undefined,
    maxSpeedKmh: speeds.length > 0 ? Math.round(Math.max(...speeds)) : undefined,
    pointCount: route?.points.length ?? 0,
  };
}

// ---------------------------------------------------------------------------
// FE-13 — novedades y sus cambios (ADR-015)
// ---------------------------------------------------------------------------

export function logbookEntryTypeFromDto(dto: LogbookEntryTypeDto): LogbookEntryType {
  return { id: dto.id, code: dto.code, label: dto.label, active: dto.active };
}

export function logbookEntryFromDto(dto: LogbookEntryDto): LogbookEntry {
  return {
    id: dto.id,
    entryTypeId: dto.entryTypeId,
    entryTypeCode: dto.entryTypeCode,
    entryTypeLabel: dto.entryTypeLabel,
    occurredAt: dto.occurredAt,
    description: dto.description,
    vehicleId: dto.vehicleId ?? undefined,
    vehiclePlate: dto.vehiclePlate ?? undefined,
    subUnitId: dto.subunitId ?? undefined,
    driverId: dto.driverId ?? undefined,
    driverName: dto.driverName ?? undefined,
    tripId: dto.tripId ?? undefined,
    registeredBy: dto.registeredBy,
    attachmentCount: dto.attachmentCount ?? undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt ?? undefined,
  };
}

/** BE-70 — la foto adjunta a una novedad. */
export function logbookAttachmentFromDto(dto: LogbookAttachmentDto): LogbookAttachment {
  return {
    id: dto.id,
    contentType: dto.contentType,
    sizeBytes: dto.sizeBytes,
    integrityHash: dto.integrityHash,
    uploadedBy: dto.uploadedBy,
    uploadedAt: dto.uploadedAt,
  };
}

/** Etiquetas de los campos que la novedad puede reportar como cambiados. */
const LOGBOOK_FIELD_LABELS: Record<string, string> = {
  description: 'Relato',
  occurredAt: 'Fecha del hecho',
  entry_type_id: 'Tipo',
  entryTypeId: 'Tipo',
  vehicle_id: 'Vehículo',
  vehicleId: 'Vehículo',
  driver_id: 'Conductor',
  driverId: 'Conductor',
  trip_id: 'Viaje',
  tripId: 'Viaje',
};

export function logbookFieldLabel(field: string): string {
  return LOGBOOK_FIELD_LABELS[field] ?? field;
}

/**
 * `changes` llega como un objeto `{campo: {from, to}}` — una forma cómoda para
 * el transporte y pésima para pintar: el orden de las claves de un objeto JSON
 * no está garantizado y no se puede recorrer con `.map` sin convertirlo. Se
 * aplana a una lista ordenada por nombre de campo, así dos correcciones
 * distintas del mismo relato se leen siempre en el mismo orden.
 *
 * A diferencia de la novedad del viaje (issue #94), esta SÍ llega ordenada y
 * con un reloj coherente: `recordedAt` es siempre reloj de pared, nunca el
 * instante declarado. Aun así se ordena aquí — un orden que el cliente no
 * verifica es un orden en el que el cliente confía sin razón.
 */
export function logbookTransitionsFromDto(items: LogbookTransitionDto[]): LogbookTransition[] {
  return items
    .map(dto => {
      const changes: LogbookChange[] = Object.entries(dto.changes ?? {})
        .map(([field, v]) => ({ field, from: v.from, to: v.to }))
        .sort((a, b) => a.field.localeCompare(b.field));
      return {
        action: dto.action,
        changes,
        actor: dto.actor,
        reason: dto.reason ?? undefined,
        recordedAt: dto.recordedAt,
      };
    })
    .sort((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt));
}

// ---------------------------------------------------------------------------
// FE-12 — gastos y recibo (ADR-015, BE-28)
// ---------------------------------------------------------------------------

export function expenseCategoryFromDto(dto: ExpenseCategoryDto): ExpenseCategory {
  return { id: dto.id, code: dto.code, label: dto.label, active: dto.active };
}

export function receiptFromDto(dto: ReceiptDto): Receipt {
  return {
    id: dto.id,
    contentType: dto.contentType,
    sizeBytes: dto.sizeBytes,
    integrityHash: dto.integrityHash,
    uploadedBy: dto.uploadedBy,
    uploadedAt: dto.uploadedAt,
  };
}

/**
 * ⚠️ `amount` se copia TAL CUAL, como texto. No se convierte a `number` en
 * ninguna parte de la app: el float64 de JavaScript no representa 0,1 con
 * exactitud y esta cifra acaba en un reporte contable. Formatear y sumar son
 * cosa de `utils/expenses.ts`, que trabaja en centavos enteros.
 */
export function expenseFromDto(dto: ExpenseDto): Expense {
  return {
    id: dto.id,
    categoryId: dto.categoryId,
    categoryCode: dto.categoryCode,
    categoryLabel: dto.categoryLabel,
    amount: dto.amount,
    currency: dto.currency,
    incurredAt: dto.incurredAt,
    vehicleId: dto.vehicleId ?? undefined,
    vehiclePlate: dto.vehiclePlate ?? undefined,
    subUnitId: dto.subunitId ?? undefined,
    driverId: dto.driverId ?? undefined,
    driverName: dto.driverName ?? undefined,
    tripId: dto.tripId ?? undefined,
    notes: dto.notes ?? undefined,
    registeredBy: dto.registeredBy,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt ?? undefined,
    receipt: dto.receipt ? receiptFromDto(dto.receipt) : undefined,
  };
}

/** Etiquetas de los campos que la novedad del gasto puede reportar como cambiados. */
const EXPENSE_FIELD_LABELS: Record<string, string> = {
  amount: 'Importe',
  incurredAt: 'Fecha del gasto',
  incurred_at: 'Fecha del gasto',
  category_id: 'Categoría',
  categoryId: 'Categoría',
  vehicle_id: 'Vehículo',
  vehicleId: 'Vehículo',
  driver_id: 'Conductor',
  driverId: 'Conductor',
  trip_id: 'Viaje',
  tripId: 'Viaje',
  notes: 'Nota',
};

export function expenseFieldLabel(field: string): string {
  return EXPENSE_FIELD_LABELS[field] ?? field;
}

/**
 * Mismo aplanado que `logbookTransitionsFromDto`: `changes` llega como un
 * objeto `{campo: {from, to}}`, cómodo para el transporte y pésimo para pintar
 * —el orden de las claves de un objeto JSON no está garantizado—. Se convierte
 * en lista ordenada por nombre de campo.
 *
 * ⚠️ El DTO llama `occurredAt` al reloj de pared de la ANOTACIÓN. Aquí pasa a
 * `recordedAt` para que no se confunda con el `incurredAt` del gasto, que es
 * cuándo se incurrió. Mezclarlos es el defecto que dejó desordenada la novedad
 * del viaje (issue #94).
 */
export function expenseTransitionsFromDto(items: ExpenseTransitionDto[]): ExpenseTransition[] {
  return items
    .map(dto => {
      const changes: ExpenseChange[] = Object.entries(dto.changes ?? {})
        .map(([field, v]) => ({ field, from: v.from, to: v.to }))
        .sort((a, b) => a.field.localeCompare(b.field));
      return {
        action: dto.action,
        changes,
        actor: dto.actor,
        reason: dto.reason ?? undefined,
        recordedAt: dto.occurredAt,
      };
    })
    .sort((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt));
}
